import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import appTheme from "./theme/appTheme";
import Header from "./Components/Header";
import Footer from "./Components/Footer";
import Home from "./Pages/Home";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import SearchResultsPage from "./Components/SearchResultpage";
import Login from "./Pages/Login";
import Signup from "./Pages/Signup";
import SignupThankYou from "./Pages/SignupThankYou";
import { AuthProvider } from "./Context/AuthContext";
import HotelDetail from "./Components/HotelDetailPage/HotelDetail";
import Payment from "./Pages/Payment";
import GuestDetail from "./Pages/GuestDetail";
import ReviewBooking from "./Pages/ReviewBooking";
import PaymentStatus from "./Pages/PaymentStatus";
import ChangePassword from "./Pages/ChangePassword";
import TransactionHistory from "./Pages/TransactionHistory";

import { useEffect } from "react";
import config from './config';
import MaintenancePage from "./Components/MaintenancePage";
import ForgotPassword from "./Pages/ForgotPassword";
import BookingDetails from "./Pages/BookingDetails";
import AboutUs from "./Pages/AboutUs";
import ContactUs from "./Pages/ContactUs";
import AdminPanel from "./Pages/AdminPanel";

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Scroll to top when location changes
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth' // Use 'auto' for instant scroll, 'smooth' for animated scroll
    });
  }, [pathname]);

  return null;
};
// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
  
  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

/** Admin panel: only users with isMasterUser from login (stored in localStorage). */
const MasterUserRoute = ({ children }) => {
  const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
  const isMasterUser = localStorage.getItem('isMasterUser') === 'true';

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }
  if (!isMasterUser) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Separate component for routes that need header
const LayoutWithHeader = ({ children }) => {
  return (
    <>
      <Header
        sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1100 }}
      />
      {children}
      <Footer />
    </>
  );
};

const HomeRoute = () => {
  const location = useLocation();
  const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
  
  // Force re-evaluation of authentication on navigation
  useEffect(() => {
    if (location.state?.reload) {
      // Clear the reload state
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  console.log("Home route check", isLoggedIn);
  
  return isLoggedIn ? (
    <LayoutWithHeader>
      <Home />
    </LayoutWithHeader>
  ) : (
    <Navigate to="/login" replace />
  );
};

function App() {
  const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';

  // Check for maintenance mode first
  if (config.isMaintenance) {
    return (
      <ThemeProvider theme={appTheme}>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <MaintenancePage />
        </LocalizationProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Router>
          <ScrollToTop />
          <AuthProvider>
            <Routes>
              {/* Initial Route */}
              <Route
                path="/"
                element={
                  <HomeRoute />
                }
              />

              {/* Protected Routes with header */}
              <Route
                path="/search"
                element={
                  <ProtectedRoute>
                    <LayoutWithHeader>
                      <SearchResultsPage />
                    </LayoutWithHeader>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/hotel-search"
                element={
                  <ProtectedRoute>
                    <LayoutWithHeader>
                      <HotelDetail />
                    </LayoutWithHeader>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payment"
                element={
                  <ProtectedRoute>
                    <LayoutWithHeader>
                      <Payment />
                    </LayoutWithHeader>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/guest-detail"
                element={
                  <ProtectedRoute>
                    <LayoutWithHeader>
                      <GuestDetail />
                    </LayoutWithHeader>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/review-booking"
                element={
                  <ProtectedRoute>
                    <LayoutWithHeader>
                      <ReviewBooking />
                    </LayoutWithHeader>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payment-status"
                element={
                  <ProtectedRoute>
                    <LayoutWithHeader>
                      <PaymentStatus />
                    </LayoutWithHeader>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/change-password"
                element={
                  <ProtectedRoute>
                    <LayoutWithHeader>
                      <ChangePassword />
                    </LayoutWithHeader>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <MasterUserRoute>
                    <LayoutWithHeader>
                      <AdminPanel />
                    </LayoutWithHeader>
                  </MasterUserRoute>
                }
              />
               <Route
              path="/booking-detail"
              element={
              <ProtectedRoute>
              <LayoutWithHeader>
              <BookingDetails />
              </LayoutWithHeader>
              </ProtectedRoute>
              }
              />
 
              <Route
                path="/transaction-history"
                element={
                  <ProtectedRoute>
                    <LayoutWithHeader>
                      <TransactionHistory />
                    </LayoutWithHeader>
                  </ProtectedRoute>
                }
              />

              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/signup-success" element={<SignupThankYou />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/contact-us" element={<ContactUs />} />

              {/* Catch all unmatched routes */}
              <Route
                path="*"
                element={
                  isLoggedIn ? (
                    <Navigate to="/" replace />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
            </Routes>
          </AuthProvider>
        </Router>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;